import React, {
  FC,
  ReactElement,
  useState,
  useContext,
  useEffect,
} from "react";
import { defineMessages, useIntl } from "react-intl";

import { Notification, SpinnerView } from "@app/components";
import BillingSummaryCard from "@app/postBill/components/billingSummaryCard";
import ActionChips from "@app/postBill/components/actionChips/actionChips";
import BillingTestCard from "@app/postBill/components/billingTestCard/billingTestCard";
import BillingInstallmentCard from "@app/postBill/components/billingInstallmentCard";
import {
  Bill,
  BillingAction,
  GetItemsResponseData,
  InstallmentItem,
  Item,
  ResponseData,
  GetStatementRequestInput,
} from "@app/postBill/models";
import {
  PostBillAPIContext,
  PostBillContext,
  UppAuthContext,
} from "@app/provider";
import { HEAP_EVENTS } from "@app/provider/types";
import { orderReadyBills, orderUpcomingBills } from "@app/postBill/utils";
import { usePostBillHeapEvent } from "@app/postBill/hooks";

import "./billingCenter.scss";
import { useNavigate } from "react-router-dom";
import { routes } from "@app/routing";
import { logMsg } from "@app/utils/logMsg";

const messages = defineMessages({
  billingCenterFirstName: {
    id: "billingCenterFirstName",
    defaultMessage: "Hi {firstName},",
  },
  billingCenterSubtitle: {
    id: "billingCenterSubtitle",
    defaultMessage: "You have {itemCount} ready to pay.",
  },
  billingCenterBillTextSingular: {
    id: "billingCenterBillTextSingular",
    defaultMessage: "bill",
  },
  billingCenterBillTextPlural: {
    id: "billingCenterBillTextPlural",
    defaultMessage: "bills",
  },
  billingDetails: {
    id: "billingDetails",
    defaultMessage: "Billing Details",
  },
  billingNoItems: {
    id: "billingNoItems",
    defaultMessage: "No outstanding balance",
  },
  billingNoItemsNotification: {
    id: "billingNoItemsNotification",
    defaultMessage:
      "We’re in the process of upgrading our billing platform.{br} During this transition period, not all test types have been moved to our new system yet. {br} If you don’t see your bill after logging in, please visit {a} to view and pay your bill.",
  },
  upcomingNoItems: {
    id: "upcomingNoItems",
    defaultMessage: "No pending bills or scheduled payments",
  },
});

const BillingCenter: FC = () => {
  const intl = useIntl();
  const [activeChip, setActiveChip] = useState<BillingAction>(
    BillingAction.READY_TO_PAY
  );
  const {
    apiProviderState: { billingData },
    getItems,
    verifyLogin,
    getCTSToken,
    loadingStates,
  } = useContext(PostBillAPIContext);
  const navigate = useNavigate();
  const { setPatientUid } = useContext(PostBillContext);
  const { profile: uppProfile } = useContext(UppAuthContext);
  const { trackPostBillHeapEvent } = usePostBillHeapEvent();
  const readyToPayCount =
    (billingData?.readyToPay.length ?? 0) +
    (billingData?.readyToPayInstallments.length ?? 0);
  const upcomingCount =
    (billingData?.upcoming.length ?? 0) +
    (billingData?.upcomingInstallments.length ?? 0);
  const [readyBills, setReadyBills] = useState<Bill[]>([]);
  const [upcomingBills, setUpcomingBills] = useState<Item[]>([]);
  const { setGuestProfileUid } = useContext(UppAuthContext);

  const ListBills = (noItemInfo: string, bills: Bill[], count: number) => {
    if (count === 0) {
      return (
        <div className="billing-center__details__no-items">{noItemInfo}</div>
      );
    }

    return (
      <>
        {bills?.map((item: Bill, index: number) => {
          if ("currentInstallmentIndex" in (item as InstallmentItem)) {
            return (
              <BillingInstallmentCard
                key={index}
                installmentItem={item as InstallmentItem}
              />
            );
          } else {
            return (
              <BillingTestCard
                key={(item as Item).lineItemUid}
                testName={(item as Item).testName}
                amount={(item as Item).balance}
                dueDate={(item as Item).dueDate}
                caseNumber={(item as Item).caseNumber}
                dateOfService={(item as Item).dateOfService}
                orderedBy={(item as Item).orderedBy}
                statementStatus={(item as Item).statementStatus}
                currency={(item as Item).currency}
                hasPdf={(item as Item).hasPdf}
                onViewStatement={() =>
                  onViewStatement(
                    (item as Item)?.orderUid,
                    (item as Item)?.lineItemUid
                  )
                }
              />
            );
          }
        })}
      </>
    );
  };

  useEffect(() => {
    setUpcomingBills(orderUpcomingBills(billingData?.upcoming as Item[]));
  }, [billingData?.upcoming]);

  useEffect(() => {
    if (uppProfile?.patientUID) {
      setPatientUid(uppProfile?.patientUID);
    }
  }, []);

  const getBillingCenterItems = async () => {
    try {
      const response: ResponseData<GetItemsResponseData> = await getItems();

      if (response.data && !uppProfile?.patientUID) {
        setPatientUid(response.data.patientUid);
        setGuestProfileUid(response.data.patientUid);
      }
      return response;
    } catch {
      logMsg(
        "error",
        "postbill.billing-center",
        "Failed to get billing center items"
      );
    }
  };

  const ctsToken = getCTSToken?.();

  useEffect(() => {
    if (!ctsToken && uppProfile?.patientUID) {
      verifyLogin();
    }

    trackPostBillHeapEvent({ eventKey: HEAP_EVENTS.upp_view_mybills_tab });
  }, []);

  useEffect(() => {
    if (ctsToken) {
      getBillingCenterItems();
    }
  }, [ctsToken]);

  useEffect(() => {
    if (!billingData) return;

    const billItems = orderReadyBills([
      ...(billingData.readyToPay ?? []),
      ...(billingData.readyToPayInstallments ?? []),
    ]);

    setReadyBills(billItems);
  }, [billingData]);

  const formTestCount = () => {
    return `${readyToPayCount} ${
      readyToPayCount === 1
        ? intl.formatMessage(messages.billingCenterBillTextSingular)
        : intl.formatMessage(messages.billingCenterBillTextPlural)
    }`;
  };

  const title: ReactElement = (
    <section className="billing-center-welcome__title">
      <h2>
        {intl.formatMessage(messages.billingCenterFirstName, {
          firstName: billingData?.patientName ?? "",
        })}
      </h2>
      <span>
        {intl.formatMessage(messages.billingCenterSubtitle, {
          itemCount: formTestCount(),
        })}
      </span>
    </section>
  );

  const handleChipClick = (chip: BillingAction) => {
    setActiveChip(chip);
  };

  const onViewStatement = async (orderUid: string, lineItemUid: string) => {
    const statementData: GetStatementRequestInput = {
      bosOrderUid: orderUid,
      bosLineItemUid: lineItemUid,
    };
    navigate(routes.billingStatementPdf, {
      state: { statementData, from: routes.billingCenter },
    });
  };

  const renderNotification = () => {
    if (
      !uppProfile?.patientUID &&
      (!billingData ||
        (billingData.readyToPay.length === 0 &&
          billingData.upcoming.length === 0 &&
          billingData.readyToPayInstallments.length === 0 &&
          billingData.upcomingInstallments.length === 0))
    ) {
      return (
        <Notification className="billing-center__notification" type="info">
          <p className="billing-center__notification__content">
            {intl.formatMessage(messages.billingNoItemsNotification, {
              br: <br />,
              a: (
                <a
                  className="billing-center__notification__content__link"
                  href="https://payment.natera.com/"
                >
                  https://payment.natera.com/
                </a>
              ),
            })}
          </p>
        </Notification>
      );
    }
  };

  return (
    <>
      <SpinnerView isLoading={loadingStates.getItems} />
      <div className="billing-center">
        {!loadingStates.verifyLogin &&
          !loadingStates.getItems &&
          renderNotification()}
        <div className="billing-center-welcome">
          {!uppProfile?.patientUID ? title : null}
          <BillingSummaryCard />
        </div>
        <div className="billing-center__details">
          <span className="billing-center__details-title">
            {intl.formatMessage(messages.billingDetails)}
          </span>
          <ActionChips
            activeChip={activeChip}
            readyToPayCount={readyToPayCount}
            upcomingCount={upcomingCount}
            onChipClick={handleChipClick}
          />
          {activeChip === BillingAction.READY_TO_PAY && (
            <>
              {ListBills(
                intl.formatMessage(messages.billingNoItems),
                readyBills,
                readyToPayCount
              )}
            </>
          )}

          {activeChip === BillingAction.UPCOMING && (
            <>
              {billingData?.upcomingInstallments?.map(
                (item: InstallmentItem, index: number) => (
                  <BillingInstallmentCard key={index} installmentItem={item} />
                )
              )}
              {ListBills(
                intl.formatMessage(messages.upcomingNoItems),
                upcomingBills,
                upcomingCount
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default BillingCenter;
